import { useEffect, useState } from 'react';
import RouteService from '~/modules/services/route-service';

/**
 * Preferences, addressable.
 *
 * The panel is a modal over whatever page you were on, so it cannot be a route of its own without
 * navigating away from that page. It is a query parameter instead: `?prefs=profile` means "open,
 * on the Profile tab". One parameter carries both facts, so there is no way for "open" and "which
 * tab" to disagree.
 *
 * The parameter is declared on the `pixwel.user` abstract state, which every signed-in page
 * inherits from, and marked `dynamic` so opening the panel does not re-enter the state underneath
 * and refetch the page behind it.
 *
 * Written with `pushRouteParams`, which inherits the current parameters — `setRouteParams` would
 * drop them, and losing a project's filters on the way into Preferences is its own bug — and adds
 * a history entry for each step. `updateRouteParams` rewrites the current entry instead, which
 * left Back with nothing to return to: it navigated off the page rather than closing the panel,
 * and the `popstate` listener below never had anything to do.
 */
export const PREFS_PARAM = 'prefs';

export const DEFAULT_TAB = 'profile';

const readParam = () => new URLSearchParams(window.location.search).get(PREFS_PARAM);

export default function usePreferencesRoute() {
	const [tab, setTab] = useState(readParam);

	// Back and forward should close and reopen the panel as the person expects, rather than
	// leaving it stuck on whatever it was when they left.
	useEffect(() => {
		const sync = () => setTab(readParam());

		window.addEventListener('popstate', sync);

		return () => window.removeEventListener('popstate', sync);
	}, []);

	const write = (value) => {
		setTab(value);
		RouteService.pushRouteParams({ [PREFS_PARAM]: value });
	};

	return {
		isOpen: !!tab,
		tab: tab || DEFAULT_TAB,
		open: (which = DEFAULT_TAB) => write(which),
		close: () => write(null),
		selectTab: (which) => write(which)
	};
}
